// Small persisted pointer that re-ties the create page to its backend
// preprocess task across refreshes. Deliberately NOT a draft system: no
// image bytes, no File/Blob, no tokens, no consent checkbox — the photo
// and the real pipeline stage are always re-read from the backend task.
// One active draft per user per browser; a new task overwrites it.

import type { AvatarEmotion } from '../../utils/avatar-emotion'

import { readAnalyticsConsent } from '../../utils/analytics-consent'
import { normalizeAvatarEmotion } from '../../utils/avatar-emotion'

export const DRAFT_SCHEMA_VERSION = 1

// Loose housekeeping bound only. Recovery always re-checks the backend
// task; expiry just keeps localStorage from accumulating dead pointers.
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60_000

const DRAFT_KEY_PREFIX = 'avatar_create_draft_v1:'

export type CreateDraftStatus = 'processing' | 'form' | 'submitting' | 'result_unknown'

// Analytics only (createFlowTelemetry): the funnel "create flow" a refreshed
// page continues, so its events keep the same create_flow_id.
export interface PersistedCreateFlow {
  id: string
  photoSeq: number
  /** Epoch ms when the current photo was selected. */
  photoStartedAt?: number
  restored?: true
  /** Milestones already reported for the current photo. */
  reported?: string[]
}

export const MAX_CREATE_FLOW_MILESTONES = 20

// Same shape the analytics sanitizer keeps; other long ids would be dropped.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/** Validates the draft field; anything unusable is treated as absent. */
export function normalizePersistedCreateFlow(value: unknown): PersistedCreateFlow | undefined {
  if (!value || typeof value !== 'object')
    return undefined
  const raw = value as Record<string, unknown>
  if (typeof raw.id !== 'string' || !UUID_PATTERN.test(raw.id))
    return undefined
  if (typeof raw.photoSeq !== 'number' || !Number.isInteger(raw.photoSeq) || raw.photoSeq < 1)
    return undefined
  const photoStartedAt = typeof raw.photoStartedAt === 'number' && Number.isFinite(raw.photoStartedAt) && raw.photoStartedAt > 0
    ? raw.photoStartedAt
    : undefined
  const reported = Array.isArray(raw.reported)
    ? raw.reported.filter((item): item is string => typeof item === 'string').slice(-MAX_CREATE_FLOW_MILESTONES)
    : []
  return {
    id: raw.id,
    photoSeq: raw.photoSeq,
    ...(photoStartedAt ? { photoStartedAt } : {}),
    ...(raw.restored === true ? { restored: true as const } : {}),
    ...(reported.length ? { reported } : {}),
  }
}

export interface CreateDraft {
  schemaVersion: number
  userId: string
  taskId: string
  attemptsUsed: number
  name: string
  emotion?: AvatarEmotion
  status: CreateDraftStatus
  // Drafts written before create flows existed simply lack it.
  createFlow?: PersistedCreateFlow
  createdAt: number
  updatedAt: number
}

const DRAFT_STATUSES: CreateDraftStatus[] = ['processing', 'form', 'submitting', 'result_unknown']

function storageKey(userId: string): string {
  return `${DRAFT_KEY_PREFIX}${userId}`
}

function getStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  }
  catch {
    return null
  }
}

// All writes share the consent gate, including late task/form checkpoints.
function saveDraft(storage: Storage, draft: CreateDraft): void {
  const { createFlow, ...functionalDraft } = draft
  const allowedFlow = readAnalyticsConsent() === true ? normalizePersistedCreateFlow(createFlow) : undefined
  try {
    storage.setItem(storageKey(draft.userId), JSON.stringify({
      ...functionalDraft,
      ...(allowedFlow ? { createFlow: allowedFlow } : {}),
    }))
  }
  catch {}
}

/** Remove analytics for every saved user without discarding their active task. */
export function clearCreateDraftAnalytics(): void {
  const storage = getStorage()
  if (!storage)
    return
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (!key?.startsWith(DRAFT_KEY_PREFIX))
        continue
      try {
        const draft = JSON.parse(storage.getItem(key) ?? '')
        if (!draft || typeof draft !== 'object' || !('createFlow' in draft))
          continue
        delete draft.createFlow
        storage.setItem(key, JSON.stringify(draft))
      }
      catch {}
    }
  }
  catch {}
}

function isValidDraft(value: unknown, userId: string): value is CreateDraft {
  if (!value || typeof value !== 'object')
    return false
  const draft = value as Record<string, unknown>
  return draft.schemaVersion === DRAFT_SCHEMA_VERSION
    && draft.userId === userId
    && typeof draft.taskId === 'string' && draft.taskId.length > 0
    && typeof draft.attemptsUsed === 'number' && Number.isFinite(draft.attemptsUsed)
    && typeof draft.name === 'string'
    && DRAFT_STATUSES.includes(draft.status as CreateDraftStatus)
    && typeof draft.createdAt === 'number'
    && typeof draft.updatedAt === 'number'
}

// Drop expired drafts for any user so a shared machine does not slowly
// collect stale pointers. Called opportunistically on read.
function sweepExpiredDrafts(storage: Storage): void {
  try {
    const staleKeys: string[] = []
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (!key || !key.startsWith(DRAFT_KEY_PREFIX))
        continue
      try {
        const parsed = JSON.parse(storage.getItem(key) ?? '') as { updatedAt?: unknown }
        const updatedAt = typeof parsed?.updatedAt === 'number' ? parsed.updatedAt : 0
        if (Date.now() - updatedAt > DRAFT_MAX_AGE_MS)
          staleKeys.push(key)
      }
      catch {
        staleKeys.push(key)
      }
    }
    staleKeys.forEach(key => storage.removeItem(key))
  }
  catch {}
}

export function readCreateDraft(userId: string): CreateDraft | null {
  const storage = getStorage()
  if (!storage || !userId)
    return null
  sweepExpiredDrafts(storage)
  try {
    const raw = storage.getItem(storageKey(userId))
    if (!raw)
      return null
    const parsed = JSON.parse(raw)
    if (!isValidDraft(parsed, userId)) {
      storage.removeItem(storageKey(userId))
      return null
    }
    const { createFlow, ...draft } = parsed
    const validCreateFlow = readAnalyticsConsent() === true ? normalizePersistedCreateFlow(createFlow) : undefined
    if (createFlow !== undefined && !validCreateFlow)
      saveDraft(storage, draft)
    return {
      ...draft,
      emotion: normalizeAvatarEmotion(parsed.emotion),
      ...(validCreateFlow ? { createFlow: validCreateFlow } : {}),
    }
  }
  catch {
    try {
      storage.removeItem(storageKey(userId))
    }
    catch {}
    return null
  }
}

// Creates or replaces the draft. Used at every activeTaskId change — the
// task id written here is always the latest run, never a prior attempt.
export function writeCreateDraft(
  userId: string,
  input: { taskId: string, attemptsUsed: number, status: CreateDraftStatus, name: string, emotion?: AvatarEmotion, createFlow?: PersistedCreateFlow },
): void {
  const storage = getStorage()
  if (!storage || !userId)
    return
  const existing = readCreateDraft(userId)
  const now = Date.now()
  const draft: CreateDraft = {
    schemaVersion: DRAFT_SCHEMA_VERSION,
    userId,
    taskId: input.taskId,
    attemptsUsed: input.attemptsUsed,
    name: input.name,
    emotion: normalizeAvatarEmotion(input.emotion),
    status: input.status,
    ...(input.createFlow ? { createFlow: input.createFlow } : {}),
    createdAt: existing?.taskId === input.taskId ? existing.createdAt : now,
    updatedAt: now,
  }
  saveDraft(storage, draft)
}

// Patches the existing draft only; a missing draft stays missing so
// late events (e.g. a name edit after discard) cannot resurrect one.
export function updateCreateDraft(
  userId: string,
  patch: Partial<Pick<CreateDraft, 'attemptsUsed' | 'name' | 'status' | 'emotion' | 'createFlow'>>,
): void {
  const storage = getStorage()
  if (!storage || !userId)
    return
  const existing = readCreateDraft(userId)
  if (!existing)
    return
  const draft: CreateDraft = { ...existing, ...patch, updatedAt: Date.now() }
  saveDraft(storage, draft)
}

export function clearCreateDraft(userId: string): void {
  const storage = getStorage()
  if (!storage || !userId)
    return
  try {
    storage.removeItem(storageKey(userId))
  }
  catch {}
}
